import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { 
  Plus, Send, ArrowDownLeft, ArrowUpRight, Clock, CheckCircle, XCircle, 
  Calendar as CalendarIcon, TrendingUp, Wallet, AlertCircle, Bell,
  Copy, Trash2, CreditCard, ChevronRight, QrCode, Users, Search, Building2
} from 'lucide-react';
import axios from 'axios';
import { API, wsUrl } from '../../utils/api';
import { useAuthContext } from '../../context/AuthContext';
import { format } from 'date-fns';
import { ZXingScanner } from '../../components/ZXingScanner';

export function PayerHome() {
  const { headers, user } = useAuthContext();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'sv' ? 'sv-SE' : 'en-US';
  
  // State
  const [analytics, setAnalytics] = useState({ today: 0, month: 0, received: 0 });
  const [pendingRequests, setPendingRequests] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [boundBuddys, setBoundBuddys] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [newPaymentOpen, setNewPaymentOpen] = useState(false);
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);
  const [scanRecipientOpen, setScanRecipientOpen] = useState(false);
  const [scanRequestPayerOpen, setScanRequestPayerOpen] = useState(false);
  const [scanRequestPayToOpen, setScanRequestPayToOpen] = useState(false);
  
  // Contact picker state
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [contactPickerTarget, setContactPickerTarget] = useState('payment'); // 'payment' or 'request'
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState([]);
  const [contactSearchLoading, setContactSearchLoading] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  
  // PayMig ID resolution in payment flow
  const [recipientResolved, setRecipientResolved] = useState(null);
  const [resolving, setResolving] = useState(false);
  
  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    recipient_name: '',
    recipient_alias: '',
    amount: '',
    description: '',
    category: '',
    isScheduled: false,
    scheduledDate: null,
    scheduledTime: '09:00'
  });
  
  // Request form state
  const [requestForm, setRequestForm] = useState({
    payer_name: '',
    payer_alias: '',
    payer_email: '',
    amount: '',
    description: '',
    category: '',
    expiry_days: 7,
    pay_to_others: false,
    pay_to_alias: ''
  });

  // WebSocket reference
  const wsRef = useRef(null);

  // Memoize fetchData to avoid recreating it on every render
  const fetchData = useCallback(async () => {
    try {
      const [
        analyticsRes,
        p2pRequestsRes,
        parentRequestsRes,
        transactionsRes,
        contactsRes,
        categoriesRes,
        bindingsRes
      ] = await Promise.all([
        axios.get(`${API}/transaction/stats?period=month`, { headers, timeout: 20000 }).catch(() => ({ data: {} })),
        axios.get(`${API}/request/received?status=PENDING`, { headers, timeout: 20000 }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/parent/requests`, { headers, timeout: 20000 }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/transaction?limit=10`, { headers, timeout: 20000 }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/contact?favorites_only=true`, { headers, timeout: 20000 }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/parent/categories`, { headers, timeout: 20000 }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/parent/children`, { headers, timeout: 20000 }).catch(() => ({ data: [] })),
      ]);
      
      setAnalytics({
        today: analyticsRes.data?.total_today || 0,
        month: analyticsRes.data?.total_sent || 0,
        received: analyticsRes.data?.total_received || 0
      });
      const p2pPending = (p2pRequestsRes.data?.items || []).map((req) => ({
        ...req,
        request_type: 'P2P'
      }));

      const buddyPending = (parentRequestsRes.data?.items || [])
        .filter((req) => req.status === 'PENDING')
        .map((req) => ({
          ...req,
          request_type: 'BUDDY',
          requester: {
            name: req.mobpay_nickname || req.mobpay_name || 'Buddy',
            alias: req.mobpay_alias || null
          },
          description: req.message || req.description || req.category_name || ''
        }));

      const mergedPending = [...buddyPending, ...p2pPending].sort((a, b) => {
        const aTime = new Date(a.created_at || a.createdAt || 0).getTime();
        const bTime = new Date(b.created_at || b.createdAt || 0).getTime();
        return bTime - aTime;
      });

      setPendingRequests(mergedPending);
      setRecentTransactions(transactionsRes.data?.items || []);
      setContacts(contactsRes.data?.items || []);
      setCategories(categoriesRes.data?.items || []);
      setBoundBuddys(bindingsRes.data || []);
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  // WebSocket connection for real-time notifications
  useEffect(() => {
    if (!user?.id) return;
    
    const url = wsUrl();
    if (!url) return;
    
    const ws = new WebSocket(url);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('WebSocket connected');
      ws.send(JSON.stringify({ type: 'subscribe', user_id: user.id }));
    };
    
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        console.log('WebSocket message:', data);
        
        // Handle incoming payment notification
        if (data.type === 'payment_received') {
          toast.success(`💰 Payment Received: ${data.amount} ${data.currency} from ${data.from_name}`, {
            description: data.description || 'Click to view details',
            duration: 8000
          });
          // Refresh data to show new transaction
          fetchData();
        }
        
        // Handle payment request notification (P2P)
        if (data.type === 'payment_request_received') {
          toast.info(`📩 Payment Request: ${data.amount} ${data.currency} from ${data.from_name}`, {
            description: data.description || 'New payment request received',
            duration: 8000
          });
          fetchData();
        }

        // Handle Buddy request notification
        if (data.type === 'request_created') {
          toast.info('📩 New Buddy request received', {
            description: data.request?.message || 'A Buddy sent a payment request',
            duration: 8000
          });
          fetchData();
        }
        
        // Handle request paid notification
        if (data.type === 'request_paid') {
          toast.success(`✅ Your request for ${data.amount} SEK was paid!`, {
            duration: 5000
          });
          fetchData();
        }
      } catch (e) {
        console.error('WebSocket message parse error:', e);
      }
    };
    
    ws.onclose = () => console.log('WebSocket disconnected');
    ws.onerror = (err) => console.error('WebSocket error:', err);
    
    return () => {
      try { ws.close(); } catch {}
    };
  }, [user?.id, fetchData]);

  useEffect(() => {
    fetchData();
    // Safety net: never leave the home screen stuck on the loader if a data
    // request stalls (slow DB / connection contention in the burst right after
    // login). The screen reveals within 8s and populates as data arrives.
    const revealTimer = setTimeout(() => setLoading(false), 8000);
    return () => clearTimeout(revealTimer);
  }, [fetchData]);

  // Check if user has any Buddys
  const hasBuddys = boundBuddys.length > 0;

  // Handle new payment
  const handleCreatePayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error(t('payer.enterValidAmount'));
      return;
    }
    if (!paymentForm.recipient_name && !paymentForm.recipient_alias) {
      toast.error(t('payer.enterRecipientOrAlias'));
      return;
    }

    try {
      let recipientAlias = paymentForm.recipient_alias;
      let recipientName = paymentForm.recipient_name;

      // If PayMig ID detected, resolve to Swish merchant number
      if (recipientResolved?.type === 'paymig') {
        recipientAlias = recipientResolved.connected_swish_id;
        recipientName = recipientName || `${recipientResolved.sub_account_name} (${recipientResolved.business_name})`;
        if (!recipientAlias) {
          toast.error(t('payer.noConnectedSwish'));
          return;
        }
      }

      if (paymentForm.isScheduled && paymentForm.scheduledDate) {
        // Create scheduled payment
        const scheduledAt = new Date(paymentForm.scheduledDate);
        const [hours, minutes] = paymentForm.scheduledTime.split(':');
        scheduledAt.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        await axios.post(`${API}/scheduled-payment`, {
          recipient_name: recipientName,
          recipient_alias: recipientAlias || undefined,
          amount: parseFloat(paymentForm.amount),
          description: paymentForm.description || undefined,
          category: paymentForm.category || undefined,
          scheduled_at: scheduledAt.toISOString(),
          reminder_channels: ['in_app', 'push', 'email']
        }, { headers });
        
        toast.success(t('payer.paymentScheduled'));
        setNewPaymentOpen(false);
        resetPaymentForm();
        fetchData();
      } else {
        // Process now - create payment record and get deeplink
        const res = await axios.post(`${API}/payment/create`, {
          recipient_name: recipientName,
          recipient_alias: recipientAlias || undefined,
          amount: parseFloat(paymentForm.amount),
          description: paymentForm.description || undefined,
          category: paymentForm.category || undefined,
          paymig_id: recipientResolved?.type === 'paymig' ? paymentForm.recipient_alias?.replace(/[-\s]/g, '') : undefined,
        }, { headers });
        
        // Store payment ID in sessionStorage for callback handling
        sessionStorage.setItem('pending_payment_id', res.data.payment_id);
        
        toast.info(t('payer.openingSwish'));
        setNewPaymentOpen(false);
        resetPaymentForm();
        
        // Redirect to Swish
        window.location.href = res.data.deeplink;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create payment');
    }
  };

  // Handle new request
  const handleCreateRequest = async () => {
    if (!requestForm.amount || parseFloat(requestForm.amount) <= 0) {
      toast.error(t('payer.enterValidAmount'));
      return;
    }

    if (!requestForm.payer_alias && !requestForm.payer_email) {
      toast.error(t('payer.enterPayerAlias'));
      return;
    }

    if (requestForm.pay_to_others && !requestForm.pay_to_alias) {
      toast.error(t('payer.enterPayToAlias'));
      return;
    }

    try {
      const res = await axios.post(`${API}/request/create`, {
        amount: parseFloat(requestForm.amount),
        currency: 'SEK',
        description: requestForm.description || undefined,
        category: requestForm.category || undefined,
        payer_alias: requestForm.payer_alias || undefined,
        payer_email: requestForm.payer_email || undefined,
        payer_name: requestForm.payer_name || undefined,
        pay_to_alias: requestForm.pay_to_others ? requestForm.pay_to_alias : undefined,
        expiry_days: requestForm.expiry_days
      }, { headers });
      
      toast.success(t('payer.requestCreated'));
      if (res.data.share_link) {
        navigator.clipboard.writeText(res.data.share_link);
        toast.info(t('payer.shareLinkCopied'));
      }
      
      setNewRequestOpen(false);
      resetRequestForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create request');
    }
  };

  // Handle pending request actions
  const handlePayRequest = async (request) => {
    if (request.request_type === 'BUDDY') {
      try {
        const res = await axios.post(`${API}/parent/requests/process`, { request_id: request._id }, { headers });
        const mode = res.data.mode || null;
        const deeplink = res.data.deeplink || null;
        const token = res.data.paymentRequestToken || null;
        openSwishImmediate(mode, token, deeplink);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to process Buddy request');
      }
      return;
    }

    const payeeTarget = request.pay_to_alias || request.requester?.alias || request.requester?.name;
    const deeplink = generateSwishDeeplink(
      payeeTarget,
      request.amount,
      request.description || `Payment to ${request.requester?.name}`
    );
    
    // Mark as in progress
    await axios.post(`${API}/request/${request._id}/pay`, {}, { headers }).catch(() => {});
    
    window.location.href = deeplink;
  };

  const handleDeclineRequest = async (request) => {
    try {
      if (request.request_type === 'BUDDY') {
        await axios.post(`${API}/parent/requests/decline`, { request_id: request._id }, { headers });
      } else {
        await axios.post(`${API}/request/${request._id}/decline`, {}, { headers });
      }
      toast.success(t('payer.requestDeclined'));
      fetchData();
    } catch (e) {
      toast.error(t('payer.failedToDecline'));
    }
  };

  // Generate Swish deeplink
  const generateSwishDeeplink = (payee, amount, message) => {
    const data = {
      version: 1,
      payee: { value: payee, editable: !payee },
      amount: { value: amount, editable: false },
      message: { value: message || '', editable: true }
    };
    const encoded = encodeURIComponent(JSON.stringify(data));
    return `swish://payment?data=${encoded}`;
  };

  const parseStaticPayload = (payload) => {
    if (!payload) return {};
    if (payload.startsWith('C') || payload.startsWith('D') || payload.startsWith('000')) {
      const payeeLength = parseInt(payload.substr(2, 2), 10);
      const payee = payload.substr(4, payeeLength);
      return { payee };
    }
    return {};
  };

  const parseSwishQR = (rawQR) => {
    if (!rawQR) return null;
    const clean = rawQR.trim();

    try {
      const parsedJSON = JSON.parse(clean);
      if (parsedJSON.payee || parsedJSON.payeeAlias || parsedJSON.payee_alias) {
        return { payee: parsedJSON.payee || parsedJSON.payeeAlias || parsedJSON.payee_alias };
      }
      if (parsedJSON.imei) return { payee: parsedJSON.imei };
    } catch (e) {}

    if (clean.startsWith('swish://') || clean.startsWith('https://')) {
      try {
        const url = new URL(clean);
        const payee = url.searchParams.get('payee') || url.searchParams.get('payee_alias') || url.searchParams.get('number');
        if (payee) return { payee };
      } catch (e) {}
    }

    if (clean.startsWith('C') || clean.startsWith('D') || clean.startsWith('000')) {
      return parseStaticPayload(clean);
    }

    const numeric = clean.match(/\d{8,20}/);
    if (numeric?.[0]) return { payee: numeric[0] };

    const aliasMatch = clean.match(/paymig|swish|alias|payee\s*[:=]\s*([a-zA-Z0-9-]{6,40})/i);
    if (aliasMatch?.[1]) return { payee: aliasMatch[1] };

    return null;
  };

  const openSwishImmediate = (mode, token, deeplink) => {
    if (mode === 'APP2APP') {
      if (deeplink) {
        window.location.href = deeplink;
        return;
      }
      toast.error('Missing Swish deeplink');
      return;
    }

    if (mode === 'TOKENIZED') {
      if (deeplink) {
        window.location.href = deeplink;
        return;
      }
      if (token) {
        const callbackurl = `${window.location.origin}/payment-return`;
        const link = `swish://paymentrequest?token=${token}&callbackurl=${encodeURIComponent(callbackurl)}`;
        window.location.href = link;
        return;
      }
      toast.error('Missing Swish token');
      return;
    }

    if (mode === 'EXTERNAL') {
      if (deeplink?.startsWith('swish://')) {
        window.location.href = deeplink;
        return;
      }
      if (token) {
        const link = `swish://paymentrequest?token=${encodeURIComponent(token)}`;
        window.location.href = link;
        return;
      }
      toast.error('Missing Swish deeplink');
      return;
    }

    if (mode === 'INTERNAL' || !mode) {
      if (token) {
        const callbackurl = `${window.location.origin}/payment-return`;
        const link = `swish://paymentrequest?token=${token}&callbackurl=${encodeURIComponent(callbackurl)}`;
        window.location.href = link;
        return;
      }
      toast.error('Missing Swish token');
    }

    if (mode === 'STATIC') {
      if (deeplink) {
        window.location.href = deeplink;
        return;
      }
      toast.error('Missing Swish deeplink');
    }
  };

  // Reset forms
  const resetPaymentForm = () => {
    setPaymentForm({
      recipient_name: '',
      recipient_alias: '',
      amount: '',
      description: '',
      category: '',
      isScheduled: false,
      scheduledDate: null,
      scheduledTime: '09:00'
    });
  };

  const resetRequestForm = () => {
    setRequestForm({
      payer_name: '',
      payer_alias: '',
      payer_email: '',
      amount: '',
      description: '',
      category: '',
      expiry_days: 7,
      pay_to_others: false,
      pay_to_alias: ''
    });
  };

  // Select contact for payment
  const selectContactForPayment = (contact) => {
    setPaymentForm(prev => ({
      ...prev,
      recipient_name: contact.name,
      recipient_alias: contact.payment_alias || ''
    }));
  };

  const selectContactForRequest = (contact) => {
    setRequestForm(prev => ({
      ...prev,
      payer_name: contact.name || contact.full_name || '',
      payer_alias: contact.payment_alias || contact.swish_alias || '',
      payer_email: contact.email || ''
    }));
  };

  // Detect if an alias is a PayMig ID (10-digit numeric) vs Swish number
  const detectIdType = (alias) => {
    if (!alias) return null;
    const clean = alias.replace(/[-\s]/g, '');
    // PayMig IDs are exactly 10 digits and don't start with 07 or +46
    if (/^\d{10}$/.test(clean) && !clean.startsWith('07') && !clean.startsWith('46')) {
      return 'paymig';
    }
    // Swedish mobile: 07XXXXXXXX or +467XXXXXXXX
    if (/^(07\d{8}|\+46\d{9,10}|46\d{9,10})$/.test(clean)) {
      return 'swish';
    }
    // Swish merchant numbers: 123XXXXXXX (starts with 123)
    if (/^123\d{7}$/.test(clean)) {
      return 'swish';
    }
    // Fallback: if all digits, could be either
    if (/^\d{7,15}$/.test(clean)) return 'swish';
    return null;
  };

  // Auto-resolve recipient when alias changes
  useEffect(() => {
    const alias = paymentForm.recipient_alias?.replace(/[-\s]/g, '');
    if (!alias || alias.length < 7) {
      setRecipientResolved(null);
      return;
    }
    const idType = detectIdType(alias);
    if (idType === 'paymig') {
      const timer = setTimeout(async () => {
        try {
          setResolving(true);
          const res = await axios.get(`${API}/business/resolve-paymig/${alias}`, { headers });
          setRecipientResolved({ type: 'paymig', ...res.data });
          // Auto-fill recipient name with sub-account/event name
          setPaymentForm(prev => ({
            ...prev,
            recipient_name: res.data.sub_account_name || prev.recipient_name,
            description: res.data.default_message || prev.description,
          }));
        } catch {
          setRecipientResolved(null);
        } finally {
          setResolving(false);
        }
      }, 400);
      return () => clearTimeout(timer);
    } else {
      setRecipientResolved(null);
    }
  }, [paymentForm.recipient_alias, headers]);

  // Contact picker search
  const searchContacts = useCallback(async (query) => {
    setContactSearchLoading(true);
    try {
      const [userRes, contactRes] = await Promise.all([
        query.length >= 2
          ? axios.get(`${API}/user/search?q=${encodeURIComponent(query)}`, { headers })
          : Promise.resolve({ data: { items: [] } }),
        axios.get(`${API}/contact${query ? `?search=${encodeURIComponent(query)}` : ''}`, { headers }),
      ]);
      setContactSearchResults(userRes.data?.items || []);
      setAllContacts(contactRes.data?.items || []);
    } catch {
      setContactSearchResults([]);
      setAllContacts([]);
    } finally {
      setContactSearchLoading(false);
    }
  }, [headers]);

  // Debounced search for contact picker
  useEffect(() => {
    if (!contactPickerOpen) return;
    const timer = setTimeout(() => searchContacts(contactSearchQuery), 300);
    return () => clearTimeout(timer);
  }, [contactSearchQuery, contactPickerOpen, searchContacts]);

  const openContactPicker = (target) => {
    setContactPickerTarget(target);
    setContactSearchQuery('');
    setContactSearchResults([]);
    setContactPickerOpen(true);
    searchContacts('');
  };

  const selectFromPicker = (item) => {
    if (contactPickerTarget === 'payment') {
      setPaymentForm(prev => ({
        ...prev,
        recipient_name: item.full_name || item.name || '',
        recipient_alias: item.swish_alias || item.payment_alias || item.paymig_id || '',
      }));
    } else {
      setRequestForm(prev => ({
        ...prev,
        payer_name: item.full_name || item.name || '',
        payer_alias: item.swish_alias || item.payment_alias || item.paymig_id || '',
        payer_email: item.email || '',
      }));
    }
    setContactPickerOpen(false);
  };

  const handleScanRecipient = (result) => {
    const txt = result?.getText?.() || '';
    const parsed = parseSwishQR(txt);
    if (parsed?.payee) {
      setPaymentForm(prev => ({ ...prev, recipient_alias: parsed.payee }));
      setScanRecipientOpen(false);
      toast.success('Payee detected');
    }
  };

  const handleScanRequestPayer = (result) => {
    const txt = result?.getText?.() || '';
    const parsed = parseSwishQR(txt);
    if (parsed?.payee) {
      setRequestForm(prev => ({ ...prev, payer_alias: parsed.payee }));
      setScanRequestPayerOpen(false);
      toast.success('Payer detected');
    }
  };

  const handleScanPayTo = (result) => {
    const txt = result?.getText?.() || '';
    const parsed = parseSwishQR(txt);
    if (parsed?.payee) {
      setRequestForm(prev => ({ ...prev, pay_to_alias: parsed.payee }));
      setScanRequestPayToOpen(false);
      toast.success('Pay-to detected');
    }
  };

  const formatDate = (isoString) => {
    try {
      return new Date(isoString).toLocaleDateString(locale, { 
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="payer-home">
      {/* Analytics Cards */}
      <div className="grid grid-cols-3 gap-3" data-testid="payer-analytics-grid">
        <Card data-testid="payer-analytics-today-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-teal-600" />
              <div>
                <p className="text-xs text-muted-foreground">Today</p>
                <p className="text-lg font-bold" data-testid="payer-analytics-today-value">{analytics.today} SEK</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="payer-analytics-month-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-xs text-muted-foreground">This Month</p>
                <p className="text-lg font-bold" data-testid="payer-analytics-month-value">{analytics.month} SEK</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50" data-testid="payer-analytics-received-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <ArrowDownLeft className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-xs text-muted-foreground">This Month Received</p>
                <p className="text-lg font-bold text-blue-700" data-testid="payer-analytics-received-value">{analytics.received} SEK</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-3 gap-3">
        {/* New Payment Button */}
        <Dialog open={newPaymentOpen} onOpenChange={setNewPaymentOpen}>
          <DialogTrigger asChild>
            <Button className="h-16 flex flex-col gap-1" data-testid="new-payment-btn">
              <CreditCard className="h-5 w-5" />
              <span className="text-xs">{t('payer.newPayment')}</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('payer.newPayment')}</DialogTitle>
              <DialogDescription>
                Send money via Swish - enter a Swish number or PayMig ID
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t('payer.recipientName')}</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={() => openContactPicker('payment')}
                    data-testid="open-contact-picker-btn"
                  >
                    <Users className="h-3.5 w-3.5" />
                    Select Contact
                  </Button>
                </div>
                <Input
                  data-testid="payment-recipient-name"
                  placeholder="John Doe"
                  value={paymentForm.recipient_name}
                  onChange={(e) => setPaymentForm(p => ({ ...p, recipient_name: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('payer.swishNumberOrPaymig')}</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="payment-recipient-alias"
                    placeholder="07XXXXXXXX or PayMig ID"
                    value={paymentForm.recipient_alias}
                    onChange={(e) => setPaymentForm(p => ({ ...p, recipient_alias: e.target.value }))}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setScanRecipientOpen(true)}
                    data-testid="scan-recipient-qr"
                  >
                    <QrCode className="h-4 w-4" />
                  </Button>
                </div>
                {/* ID type detection badge */}
                {paymentForm.recipient_alias && detectIdType(paymentForm.recipient_alias) && (
                  <div className="flex items-center gap-2">
                    <Badge variant={detectIdType(paymentForm.recipient_alias) === 'paymig' ? 'default' : 'secondary'} className="text-xs" data-testid="id-type-badge">
                      {detectIdType(paymentForm.recipient_alias) === 'paymig' ? (
                        <><Building2 className="h-3 w-3 mr-1" />PayMig ID</>
                      ) : (
                        <>Swish</>
                      )}
                    </Badge>
                    {resolving && <span className="text-xs text-muted-foreground">{t('payer.resolving')}</span>}
                  </div>
                )}
                {/* Resolved PayMig info */}
                {recipientResolved?.type === 'paymig' && (
                  <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs" data-testid="paymig-resolved-info">
                    <p className="text-emerald-700">{recipientResolved.business_name} ({recipientResolved.sub_account_type})</p>
                    <p className="text-emerald-600">Swish: {recipientResolved.connected_swish_id}</p>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>{t('payer.amountRequired')}</Label>
                <Input
                  data-testid="payment-amount"
                  type="number"
                  placeholder="100"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(p => ({ ...p, amount: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('payer.description')}</Label>
                <Textarea
                  data-testid="payment-description"
                  placeholder={t('payer.whatIsThisFor')}
                  value={paymentForm.description}
                  onChange={(e) => setPaymentForm(p => ({ ...p, description: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('payer.category')}</Label>
                <Select 
                  value={paymentForm.category} 
                  onValueChange={(v) => setPaymentForm(p => ({ ...p, category: v }))}
                >
                  <SelectTrigger data-testid="payment-category-select">
                    <SelectValue placeholder={t('payer.selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('payer.noCategory')}</SelectItem>
                    {categories.map(c => (
                      <SelectItem key={c._id || c.id} value={c.name}>
                        {c.icon} {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Schedule toggle */}
              <div className="flex items-center justify-between pt-2 border-t">
                <Label>{t('payer.scheduleForLater')}</Label>
                <Switch
                  data-testid="schedule-toggle"
                  checked={paymentForm.isScheduled}
                  onCheckedChange={(checked) => setPaymentForm(p => ({ ...p, isScheduled: checked }))}
                />
              </div>

              {paymentForm.isScheduled && (
                <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>{t('payer.date')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="payment-schedule-date-button">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {paymentForm.scheduledDate ? format(paymentForm.scheduledDate, 'PPP') : t('payer.pickDate')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={paymentForm.scheduledDate}
                          onSelect={(date) => setPaymentForm(p => ({ ...p, scheduledDate: date }))}
                          disabled={(date) => date < new Date()}
                          data-testid="payment-schedule-calendar"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('payer.time')}</Label>
                    <Input
                      type="time"
                      value={paymentForm.scheduledTime}
                      onChange={(e) => setPaymentForm(p => ({ ...p, scheduledTime: e.target.value }))}
                      data-testid="payment-schedule-time"
                    />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setNewPaymentOpen(false); resetPaymentForm(); setRecipientResolved(null); }} data-testid="cancel-payment-btn">
                {t('common.cancel')}
              </Button>
              <Button data-testid="submit-payment-btn" onClick={handleCreatePayment}>
                {paymentForm.isScheduled ? (
                  <><Clock className="h-4 w-4 mr-2" />{t('payer.schedule')}</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" />{t('payer.processInSwish')}</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Request Button */}
        <Dialog open={newRequestOpen} onOpenChange={setNewRequestOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="h-16 flex flex-col gap-1" data-testid="new-request-btn">
              <ArrowDownLeft className="h-5 w-5" />
              <span className="text-xs">{t('payer.newRequest')}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('payer.newRequest')}</DialogTitle>
              <DialogDescription>
                Request money from someone via PayMig
              </DialogDescription>
            </DialogHeader>
            {contacts.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Quick Contacts</Label>
                <div className="flex flex-wrap gap-2">
                  {contacts.slice(0, 4).map(c => (
                    <Button
                      key={c._id}
                      variant="outline"
                      size="sm"
                      onClick={() => selectContactForRequest(c)}
                      data-testid={`request-contact-${c._id}`}
                    >
                      {c.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('payer.fromName')}</Label>
                <Input
                  data-testid="request-payer-name"
                  placeholder="Who should pay?"
                  value={requestForm.payer_name}
                  onChange={(e) => setRequestForm(p => ({ ...p, payer_name: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('payer.swishOrPaymig')}</Label>
                <Input
                  data-testid="request-payer-alias"
                  placeholder="0701234567"
                  value={requestForm.payer_alias}
                  onChange={(e) => setRequestForm(p => ({ ...p, payer_alias: e.target.value }))}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setScanRequestPayerOpen(true)}
                  data-testid="scan-request-payer-qr"
                >
                  <QrCode className="h-4 w-4 mr-2" /> {t('common.scanQr')}
                </Button>
              </div>

              <div className="space-y-2">
                <Label>{t('payer.category')}</Label>
                <Select
                  value={requestForm.category}
                  onValueChange={(v) => setRequestForm(p => ({ ...p, category: v }))}
                >
                  <SelectTrigger data-testid="request-category-select">
                    <SelectValue placeholder={t('payer.selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.name}>
                        {cat.icon} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{t('payer.amountRequired')}</Label>
                <Input
                  data-testid="request-amount"
                  type="number"
                  placeholder="100"
                  value={requestForm.amount}
                  onChange={(e) => setRequestForm(p => ({ ...p, amount: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  data-testid="request-description"
                  placeholder="What's this for?"
                  value={requestForm.description}
                  onChange={(e) => setRequestForm(p => ({ ...p, description: e.target.value }))}
                />
              </div>

              {/* Pay-to-others hidden in V5.0 */}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setNewRequestOpen(false); resetRequestForm(); }} data-testid="cancel-request-btn">
                Cancel
              </Button>
              <Button data-testid="submit-request-btn" onClick={handleCreateRequest}>
                <Send className="h-4 w-4 mr-2" />
                Create Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pending Requests Button */}
        <Dialog open={pendingDialogOpen} onOpenChange={setPendingDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              variant={pendingRequests.length > 0 ? 'default' : 'outline'} 
              className={`h-16 flex flex-col gap-1 ${pendingRequests.length > 0 ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
              data-testid="pending-requests-btn"
            >
              <Bell className="h-5 w-5" />
              <span className="text-xs">{t('payer.pending')} ({pendingRequests.length})</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('payer.pending')}</DialogTitle>
              <DialogDescription>
                Payment requests waiting for your action
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-96 overflow-y-auto py-4">
              {pendingRequests.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No pending requests</p>
              ) : (
                pendingRequests.map((req) => (
                  <Card key={req._id} data-testid={`pending-request-${req._id}`}>
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{req.amount} SEK</p>
                          <p className="text-sm text-muted-foreground">
                            From: {req.requester?.name || 'Unknown'}
                          </p>
                          {req.description && (
                            <p className="text-xs text-muted-foreground">{req.description}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handlePayRequest(req)} data-testid={`pay-request-${req._id}`}>
                            Pay
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeclineRequest(req)} data-testid={`decline-request-${req._id}`}>
                            Decline
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

      </div>

        <Dialog open={scanRecipientOpen} onOpenChange={setScanRecipientOpen}>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>{t('payer.scanRecipientQr')}</DialogTitle>
              <DialogDescription>{t('payer.scanRecipientDesc')}</DialogDescription>
            </DialogHeader>
            <ZXingScanner onScan={handleScanRecipient} onClose={() => setScanRecipientOpen(false)} />
          </DialogContent>
        </Dialog>

        <Dialog open={scanRequestPayerOpen} onOpenChange={setScanRequestPayerOpen}>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>{t('payer.scanPayerQr')}</DialogTitle>
              <DialogDescription>{t('payer.scanPayerDesc')}</DialogDescription>
            </DialogHeader>
            <ZXingScanner onScan={handleScanRequestPayer} onClose={() => setScanRequestPayerOpen(false)} />
          </DialogContent>
        </Dialog>

        <Dialog open={scanRequestPayToOpen} onOpenChange={setScanRequestPayToOpen}>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>{t('payer.scanPayToQr')}</DialogTitle>
              <DialogDescription>{t('payer.scanPayToDesc')}</DialogDescription>
            </DialogHeader>
            <ZXingScanner onScan={handleScanPayTo} onClose={() => setScanRequestPayToOpen(false)} />
          </DialogContent>
        </Dialog>

      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">{t('payer.recentActivity')}</CardTitle>
        </CardHeader>
        <CardContent>
          {recentTransactions.length === 0 ? (
            <p className="text-center text-muted-foreground py-4" data-testid="payer-no-recent-transactions">{t('payer.noRecentTransactions')}</p>
          ) : (
            <div className="space-y-2">
              {recentTransactions.slice(0, 5).map((txn) => {
                const currentUserId = user?.id;
                const isSent = txn.from_user_id === currentUserId;
                const statusColor = txn.status === 'COMPLETED' ? 'text-green-600' 
                  : txn.status === 'PENDING' ? 'text-orange-600' 
                  : 'text-red-600';
                return (
                  <div key={txn._id || txn.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50" data-testid={`recent-transaction-${txn._id || txn.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${isSent ? 'bg-red-100' : 'bg-green-100'}`}>
                        {isSent ? (
                          <ArrowUpRight className="h-4 w-4 text-red-600" />
                        ) : (
                          <ArrowDownLeft className="h-4 w-4 text-green-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {isSent ? txn.to_name : txn.from_name || 'Unknown'}
                        </p>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs ${statusColor}`}>
                            {txn.status === 'COMPLETED' ? '✓' : txn.status === 'PENDING' ? '⏳' : '✕'} {txn.status}
                          </span>
                          <span className="text-xs text-muted-foreground">• {formatDate(txn.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <p className={`font-medium ${isSent ? 'text-red-600' : 'text-green-600'}`} data-testid={`recent-transaction-amount-${txn._id || txn.id}`}>
                      {isSent ? '-' : '+'}{txn.amount} SEK
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Managed Users Section - Shows users you're monitoring */}
      <ManagedUsersSection headers={headers} onPayUser={(info) => {
        // Pre-fill payment form with managed user data
        setPaymentForm(prev => ({
          ...prev,
          recipient_name: info.name,
          recipient_alias: info.alias,
          amount: '',
          description: '',
        }));
        setRecipientResolved(null);
        setNewPaymentOpen(true);
      }} />

      {/* Business Invitations Section */}
      <BusinessInvitationsSection headers={headers} />

      {/* Contact Picker Dialog */}
      <Dialog open={contactPickerOpen} onOpenChange={setContactPickerOpen}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('payer.selectContact')}</DialogTitle>
            <DialogDescription>
              {t('payer.searchByNameEmail')}
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="contact-picker-search"
              placeholder={t('payer.searchContacts')}
              value={contactSearchQuery}
              onChange={(e) => setContactSearchQuery(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 max-h-[50vh] space-y-1" data-testid="contact-picker-list">
            {contactSearchLoading && (
              <p className="text-center text-sm text-muted-foreground py-4">{t('common.loading')}</p>
            )}

            {/* My Contacts */}
            {allContacts.length > 0 && (
              <>
                <p className="text-xs font-semibold text-muted-foreground px-1 pt-2">{t('payer.myContacts')}</p>
                {allContacts.map(c => (
                  <button
                    key={c._id || c.id}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-100 transition-colors text-left"
                    onClick={() => selectFromPicker(c)}
                    data-testid={`contact-pick-${c._id || c.id}`}
                  >
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-700 shrink-0">
                      {(c.name || c.full_name || '?')[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{c.name || c.full_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.payment_alias || c.swish_alias || c.email}</p>
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* Platform Users */}
            {contactSearchResults.length > 0 && (
              <>
                <p className="text-xs font-semibold text-muted-foreground px-1 pt-3">{t('payer.platformUsers')}</p>
                {contactSearchResults.map(u => (
                  <button
                    key={u.id}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-100 transition-colors text-left"
                    onClick={() => selectFromPicker(u)}
                    data-testid={`platform-user-pick-${u.id}`}
                  >
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
                      u.user_type === 'BUSINESS' || u.user_type === 'SUB_ACCOUNT' ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'
                    }`}>
                      {u.user_type === 'BUSINESS' || u.user_type === 'SUB_ACCOUNT' ? <Building2 className="h-4 w-4" /> : (u.full_name || '?')[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{u.full_name}{u.company_name ? ` (${u.company_name})` : ''}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {u.paymig_id ? `PayMig: ${u.paymig_id}` : ''}{u.swish_alias ? ` Swish: ${u.swish_alias}` : ''}
                      </p>
                    </div>
                    {u.user_type && (
                      <Badge variant="secondary" className="text-[10px] shrink-0">
                        {u.user_type === 'SUB_ACCOUNT' ? 'Sub' : u.user_type}
                      </Badge>
                    )}
                  </button>
                ))}
              </>
            )}

            {!contactSearchLoading && allContacts.length === 0 && contactSearchResults.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-8">
                {contactSearchQuery ? t('common.noResults') : t('payer.noContactsYet')}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ManagedUsersSection({ headers, onPayUser }) {
  const [managed, setManaged] = useState([]);
  const [myInvitations, setMyInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMu, setExpandedMu] = useState(null);
  const [muTransactions, setMuTransactions] = useState({});
  const [txnLoading, setTxnLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [managedRes, invRes] = await Promise.all([
        axios.get(`${API}/managed-users`, { headers }),
        axios.get(`${API}/managed-users/invitations`, { headers }),
      ]);
      setManaged(managedRes.data.items || []);
      setMyInvitations(invRes.data.items || []);
    } catch {} finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const respondToInvitation = async (invId, accept) => {
    try {
      await axios.post(`${API}/managed-users/invitations/${invId}/respond`, { accept }, { headers });
      toast.success(accept ? 'Invitation accepted' : 'Invitation declined');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to respond');
    }
  };

  const toggleMuTransactions = async (muId) => {
    if (expandedMu === muId) { setExpandedMu(null); return; }
    setExpandedMu(muId);
    if (!muTransactions[muId]) {
      setTxnLoading(true);
      try {
        const res = await axios.get(`${API}/managed-users/${muId}/transactions?limit=10`, { headers });
        setMuTransactions(prev => ({ ...prev, [muId]: res.data?.items || [] }));
      } catch {
        setMuTransactions(prev => ({ ...prev, [muId]: [] }));
      } finally { setTxnLoading(false); }
    }
  };

  const renderAvatar = (mu) => {
    const icon = mu.user_info?.profile_icon;
    if (icon && (icon.startsWith('http://') || icon.startsWith('https://'))) {
      return <img src={icon} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />;
    }
    const letter = mu.nickname?.charAt(0) || mu.user_info?.full_name?.charAt(0) || '?';
    return (
      <div className="h-8 w-8 rounded-full bg-blue-200 flex items-center justify-center text-sm font-medium shrink-0">
        {letter}
      </div>
    );
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  if (loading || (managed.length === 0 && myInvitations.length === 0)) return null;

  return (
    <Card className="border-blue-200 bg-blue-50" data-testid="managed-users-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-blue-800">
          Managed Users ({managed.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Invitations received (where I'm the target) */}
        {myInvitations.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-blue-700">Monitoring Requests</p>
            {myInvitations.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between p-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`mu-invitation-${inv.id}`}>
                <div>
                  <p className="text-sm font-medium">{inv.owner_info?.full_name || inv.nickname || 'Someone'}</p>
                  <p className="text-xs text-muted-foreground">Wants to monitor your account</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => respondToInvitation(inv.id, false)} data-testid={`mu-decline-${inv.id}`}>
                    <XCircle className="h-3.5 w-3.5 mr-1" /> Decline
                  </Button>
                  <Button size="sm" onClick={() => respondToInvitation(inv.id, true)} data-testid={`mu-accept-${inv.id}`}>
                    <CheckCircle className="h-3.5 w-3.5 mr-1" /> Accept
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Managed users list with spending metrics */}
        {managed.length > 0 && (
          <div className="space-y-2">
            {managed.map((mu) => (
              <div key={mu.id} className="bg-white rounded-lg overflow-hidden" data-testid={`managed-user-${mu.id}`}>
                <div className="flex items-center gap-2 p-2.5">
                  <button className="flex items-center gap-2 flex-1 min-w-0 text-left hover:bg-gray-50 transition-colors rounded"
                    onClick={() => toggleMuTransactions(mu.id)} data-testid={`mu-toggle-${mu.id}`}>
                    {renderAvatar(mu)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium truncate">{mu.nickname}</p>
                        {mu.status !== 'ACCEPTED' && (
                          <Badge variant="secondary" className="text-[10px]">{mu.status}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-blue-700 font-medium">{mu.spending?.month_total || 0} / {mu.spending?.budget_limit || 0} SEK</span>
                        <span className="text-muted-foreground">({mu.spending?.month_count || 0} txns)</span>
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {/* Pay button */}
                    <Button
                      size="sm"
                      variant="default"
                      className="h-7 px-3 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPayUser?.({
                          name: mu.nickname || mu.user_info?.full_name || '',
                          alias: mu.user_info?.swish_payee_alias || mu.user_info?.paymig_id || '',
                          budgetLimit: mu.spending?.budget_limit || 0,
                          monthSpent: mu.spending?.month_total || 0,
                        });
                      }}
                      data-testid={`pay-managed-${mu.id}`}
                    >
                      <CreditCard className="h-3 w-3 mr-1" />
                      Pay
                    </Button>
                    {/* Budget progress bar */}
                    <div className="w-12">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${(mu.spending?.progress_pct || 0) > 80 ? 'bg-rose-500' : 'bg-blue-500'}`}
                          style={{ width: `${Math.min(mu.spending?.progress_pct || 0, 100)}%` }} />
                      </div>
                      <p className="text-[10px] text-center text-muted-foreground mt-0.5">{mu.spending?.progress_pct || 0}%</p>
                    </div>
                  </div>
                </div>

                {/* Expanded: Recent transactions to this user */}
                {expandedMu === mu.id && (
                  <div className="px-3 pb-2 border-t">
                    <p className="text-xs font-medium text-muted-foreground py-1.5">Recent Transactions</p>
                    {txnLoading ? (
                      <p className="text-xs text-center text-muted-foreground py-2">Loading...</p>
                    ) : (muTransactions[mu.id] || []).length === 0 ? (
                      <p className="text-xs text-center text-muted-foreground py-2">No transactions yet</p>
                    ) : (
                      <div className="space-y-1">
                        {(muTransactions[mu.id] || []).slice(0, 10).map((txn, i) => (
                          <div key={txn.trans_id || i} className="flex items-center justify-between py-1 text-xs" data-testid={`mu-txn-${i}`}>
                            <div>
                              <span className="text-muted-foreground">{formatDate(txn.created_at)}</span>
                              {txn.description && <span className="ml-1.5 text-muted-foreground">{txn.description}</span>}
                            </div>
                            <span className="font-medium">{txn.amount} SEK</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function BusinessInvitationsSection({ headers }) {
  const [invitations, setInvitations] = useState([]);
  const [moderatedAccounts, setModeratedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [invRes, accRes] = await Promise.all([
        axios.get(`${API}/business/moderator/invitations`, { headers }),
        axios.get(`${API}/business/moderator/accounts`, { headers }),
      ]);
      setInvitations(invRes.data?.items || []);
      setModeratedAccounts(accRes.data?.items || []);
    } catch {
      // Not all users will have access
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const respond = async (invId, accept) => {
    try {
      await axios.post(`${API}/business/moderator/invitations/${invId}/respond?accept=${accept}`, {}, { headers });
      toast.success(accept ? 'Invitation accepted' : 'Invitation declined');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to respond');
    }
  };

  if (loading || (invitations.length === 0 && moderatedAccounts.length === 0)) return null;

  return (
    <Card data-testid="business-invitations-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Business Invitations
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Pending invitations */}
        {invitations.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">Pending Invitations</p>
            {invitations.map(inv => (
              <div key={inv.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`pending-invitation-${inv.id}`}>
                <div>
                  <p className="text-sm font-medium">{inv.business_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {inv.sub_account_name} ({inv.sub_account_type?.toLowerCase()})
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => respond(inv.id, false)} data-testid={`decline-inv-${inv.id}`}>
                    <XCircle className="h-3.5 w-3.5 mr-1" /> Decline
                  </Button>
                  <Button size="sm" onClick={() => respond(inv.id, true)} data-testid={`accept-inv-${inv.id}`}>
                    <CheckCircle className="h-3.5 w-3.5 mr-1" /> Accept
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Moderated accounts — show totals only (no transaction details) */}
        {moderatedAccounts.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">Moderated Accounts</p>
            {moderatedAccounts.map(acc => (
              <div key={acc.sub_account_id} className="p-3 bg-gray-50 rounded-lg" data-testid={`moderated-account-${acc.sub_account_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <p className="text-sm font-medium">{acc.sub_account_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {acc.business_name} · {acc.sub_account_type?.toLowerCase()}
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-xs">Moderator</Badge>
                </div>
                {/* Totals to-date */}
                <div className="flex items-center gap-4 mt-2 text-xs">
                  <span className="text-emerald-700 font-medium" data-testid={`mod-received-${acc.sub_account_id}`}>
                    Received: {(acc.total_received || 0).toLocaleString()} SEK
                  </span>
                  <span className="text-rose-700 font-medium" data-testid={`mod-sent-${acc.sub_account_id}`}>
                    Paid: {(acc.total_sent || 0).toLocaleString()} SEK
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
